"use client";

import { FeedEvents, trackEvent } from "@/analytics";
import { PostalCodeData, getIpData, lookupZipcodeData } from "@/data/zipcode";
import { SentryFeatureTags, logException } from "@/analytics/sentry";
import {
  ShowWithComedian,
  getPrimaryShowComedian,
  processShowData,
} from "@/data/shows";
import { useEffect, useMemo, useRef, useState } from "react";

import { Database } from "@punchup/data/supabase.types";
import { ExternalLink } from "lucide-react";
import Link from "next/link";
import { ResponsiveShowRow } from "@/components/shows";
import { cn } from "@/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import { isPlaceholderComedian } from "@/data/comedians";
import { getApiClient } from "@punchup/utils";
import { useGate } from "statsig-react";
import { useMediaQuery } from "@/components/common/hooks/useMediaQuery";
import useSession from "@/hooks/useSession";
import { useSubscriptions } from "@/hooks/useSubscriptions";

// Utility functions
const isPlaceholderShow = (show: ShowWithComedian): boolean => {
  // Filter out any show where the main comedian is a placeholder
  // BUT keep legitimate showcase shows that have real comedians in show_comedians array
  const mainComedianName = getPrimaryShowComedian(show)?.display_name;
  if (mainComedianName && isPlaceholderComedian(mainComedianName)) {
    // If it's a showcase show with real comedians, keep it
    if (show.show_comedians && show.show_comedians.length > 0) {
      // Check if ANY of the show comedians are real (not placeholders)
      const hasRealComedians = show.show_comedians.some(
        (comedian) => !isPlaceholderComedian(comedian.display_name)
      );
      // Only filter out if ALL comedians are placeholders
      return !hasRealComedians;
    }
    // For single-comedian shows with placeholder comedian, filter them out
    return true;
  }

  return false;
};

const shouldSkipGrouping = (show: ShowWithComedian): boolean => {
  // Skip grouping for showcase shows (multiple comedians)
  // These should be rendered individually to show all comedians
  if (show.show_comedians && show.show_comedians.length > 1) {
    return true;
  }

  // Skip grouping for shows with placeholder comedians - render them individually
  // This prevents legitimate shows like "Rubee Tuesdays", "Chocolate Sundaes"
  // from being grouped together under "Lineup Coming Soon"
  const mainComedianName = getPrimaryShowComedian(show)?.display_name;
  if (mainComedianName && isPlaceholderComedian(mainComedianName)) {
    return true;
  }

  return false;
};

function UpcomingShowsSkeleton() {
  return (
    <div className="my-2 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
      <div className="space-y-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4 p-2">
            <div className="h-10 w-10 bg-gray-300 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
      {/* Add a bit more height to potentially match loaded state better */}
      <div className="h-5"></div>
    </div>
  );
}

export default function UpcomingShowsFeedUnit({
  brandMomentEnabled = false,
}: {
  /**
   * True when the feed's brand moment is on: at xl+ the desktop rail renders
   * its own shows-near-you module and this unit is display-hidden, so the
   * whole location/shows fetch chain is skipped — not just the paint.
   */
  brandMomentEnabled?: boolean;
}) {
  const supabase = createClientComponentClient<Database>();
  const session = useSession();
  const userId = session?.user?.id; // Stable dependency for user state, crucial
  const { subscriptions } = useSubscriptions();

  const [location, setLocation] = useState<PostalCodeData | null>(null);
  const [buyTicketsShowId, setBuyTicketsShowId] = useState<string>("");
  const [showsNearby, setShowsNearby] = useState<ShowWithComedian[] | null>(
    null
  );
  const [visiblePages, setVisiblePages] = useState(1);
  // Adding for shimmer on load
  const [isLoading, setIsLoading] = useState(true);
  const [errorOccurred, setErrorOccurred] = useState(false);

  const { value: redesignEnabled } = useGate("feed_shows_nearby_refinements");
  const isXl = useMediaQuery("(min-width: 1280px)");
  // While the brand-moment rail owns shows-near-you (xl+), this unit is
  // display-hidden (`xl:hidden` in FeedPostsV2) — its fetches would be wasted.
  const railOwnsShows = brandMomentEnabled && isXl;

  // Ref to track the current fetch request to prevent race conditions if userId changes rapidly
  const currentFetchId = useRef(0);

  const SEARCH_RADIUS_MILES = 50;
  const SEARCH_DAYS_LIMIT = redesignEnabled ? 60 : 7; // for the redesign, this number doesn't matter much because we limit the number of shows
  const INITIAL_SHOWS = redesignEnabled ? 3 : 2;
  const SHOWS_PER_PAGE = 5;
  const MAX_CLICKS_BEFORE_REDIRECT = 3;

  useEffect(() => {
    // Increment fetch ID for this attempt (also invalidates any in-flight
    // fetch from a previous run of this effect)
    const fetchId = ++currentFetchId.current;

    // Rail owns this surface right now — skip the DB/IP/shows chain entirely.
    // Resizing below xl re-runs the effect and fetches then.
    if (railOwnsShows) {
      setIsLoading(false);
      return;
    }

    // Reset state before fetching for the current userId
    setIsLoading(true);
    setErrorOccurred(false);

    const fetchLocationData = async () => {
      let userZipcode: string | undefined;
      let userCountry: string | undefined;

      try {
        // 1. DB Lookup (if logged in)
        if (userId) {
          const { data: existingRow, error: locationError } = await supabase
            .from("user_zipcodes")
            .select("zipcode,country_code")
            .eq("user", userId)
            .eq("from_ip", false)
            .maybeSingle();

          if (locationError) {
            logException(locationError, {
              extra: { userId: userId ?? "logged-out" },
              tags: {
                feature: SentryFeatureTags.Feed,
              },
            });
          }
          if (existingRow?.zipcode && existingRow?.country_code) {
            userZipcode = existingRow.zipcode;
            userCountry = existingRow.country_code;
          }
        }

        // 2. IP Lookup (if needed)
        if (!userZipcode || !userCountry) {
          const ipData = await getIpData();
          if (ipData?.postalCode && ipData?.countryCode) {
            userZipcode = ipData.postalCode;
            userCountry = ipData.countryCode;
          }
        }

        // 3. Bail if no location found
        if (!userZipcode || !userCountry) {
          // Only update state if this is the latest fetch attempt
          if (fetchId === currentFetchId.current) {
            setShowsNearby([]); // Set empty array
            setLocation(null);
            setIsLoading(false);
          }
          return;
        }

        // 4. Lookup Zip Data & Fetch Shows (concurrently)
        const [locationDataResult, showsResponse] = await Promise.allSettled([
          lookupZipcodeData(userZipcode, userCountry),
          getApiClient().get<ShowWithComedian[]>(
            `/api/shows?postalCode=${userZipcode}&countryCode=${userCountry}&radius=${SEARCH_RADIUS_MILES}&limitDays=${SEARCH_DAYS_LIMIT}`
          ),
        ]);

        // Check if still the latest fetch before updating state
        if (fetchId !== currentFetchId.current) {
          return;
        }

        let fetchedLocation: PostalCodeData | null = null;
        if (locationDataResult.status === "fulfilled") {
          fetchedLocation = locationDataResult.value;
        } else {
          logException(locationDataResult.reason, {
            extra: { userId: userId ?? "logged-out" },
            tags: {
              feature: SentryFeatureTags.Feed,
            },
          });
        }

        let fetchedShows: ShowWithComedian[] = [];
        if (showsResponse.status === "fulfilled") {
          fetchedShows = showsResponse.value;
        } else {
          logException(showsResponse.reason, {
            extra: { userId: userId ?? "logged-out" },
            tags: {
              feature: SentryFeatureTags.Feed,
            },
          });
          throw showsResponse.reason;
        }

        setLocation(fetchedLocation);
        setShowsNearby(fetchedShows);
      } catch (error) {
        // Check if still the latest fetch before updating state
        if (fetchId === currentFetchId.current) {
          setErrorOccurred(true);
          logException(error, {
            extra: { userId: userId ?? "logged-out" },
            tags: {
              feature: SentryFeatureTags.Feed,
            },
          });
          setLocation(null);
          setShowsNearby([]);
        }
      } finally {
        if (fetchId === currentFetchId.current) {
          setIsLoading(false);
        }
      }
    };

    fetchLocationData();

    // Cleanup function: If the component unmounts or userId changes
    // *before* the fetch completes, incrementing the ref ensures the
    // stale fetch won't update the state.
    return () => {
      currentFetchId.current++;
    };
  }, [userId, supabase, SEARCH_DAYS_LIMIT, railOwnsShows]);

  // Filter out placeholder shows and comedian-less shows first. The
  // /api/shows web path includes shows with no comedian pairings; this unit
  // renders every show under a comedian row, so those can't be displayed here.
  const filteredShows = useMemo(() => {
    if (!showsNearby) return [];

    const filtered = showsNearby.filter(
      (show) => getPrimaryShowComedian(show) && !isPlaceholderShow(show)
    );

    return filtered;
  }, [showsNearby]);

  // Group shows by comedian (properly handling showcase shows)
  const groupedShows = useMemo(() => {
    if (!filteredShows) return [];

    const groupedByComedian = filteredShows.reduce(
      (acc, show) => {
        // Skip grouping for showcase shows and placeholder comedians
        if (shouldSkipGrouping(show)) {
          return acc;
        }

        // For regular single-comedian shows, group by the primary comedian
        // (first pairing)
        const comedian = getPrimaryShowComedian(show);
        if (!comedian) {
          // filteredShows already dropped comedian-less shows; this narrows
          // the type and guards against a future unfiltered caller.
          return acc;
        }
        if (!acc[comedian.id]) {
          acc[comedian.id] = {
            comedian,
            shows: [],
          };
        }
        acc[comedian.id].shows.push(show);

        return acc;
      },
      {} as Record<
        string,
        {
          comedian: NonNullable<ShowWithComedian["comedian"]>;
          shows: ShowWithComedian[];
        }
      >
    );

    // Sort comedians by nearest show date
    return Object.values(groupedByComedian).sort((a, b) => {
      const aEarliestDate = Math.min(
        ...a.shows.map((s) => dayjs(s.datetime).valueOf())
      );
      const bEarliestDate = Math.min(
        ...b.shows.map((s) => dayjs(s.datetime).valueOf())
      );
      return aEarliestDate - bEarliestDate;
    });
  }, [filteredShows]);

  // --- Render Logic ---

  if (isLoading) {
    return <UpcomingShowsSkeleton />;
  }

  if (errorOccurred || !filteredShows || filteredShows.length === 0) {
    return null;
  }

  const shouldRedirect = visiblePages > MAX_CLICKS_BEFORE_REDIRECT;

  const buttonClassName =
    "py-4 w-full mt-4 border btn-outlined border-slate-gray-900 text-slate-gray-900 md:w-[unset]";

  if (redesignEnabled) {
    // Get individual shows (showcase and placeholder comedian shows)
    const individualShows = filteredShows.filter((show) =>
      shouldSkipGrouping(show)
    );

    // Create combined list of all items to display with sort keys
    const allItems: Array<{
      type: "individual" | "group";
      item:
        | ShowWithComedian
        | {
            comedian: NonNullable<ShowWithComedian["comedian"]>;
            shows: ShowWithComedian[];
          };
      sortKey: number;
    }> = [];

    // Add individual shows
    individualShows.forEach((show) => {
      allItems.push({
        type: "individual",
        item: show,
        sortKey: dayjs(show.datetime).valueOf(),
      });
    });

    // Add grouped shows
    groupedShows.forEach((group) => {
      const earliestDate = Math.min(
        ...group.shows.map((s) => dayjs(s.datetime).valueOf())
      );
      allItems.push({
        type: "group",
        item: group,
        sortKey: earliestDate,
      });
    });

    // Sort all items by date
    allItems.sort((a, b) => a.sortKey - b.sortKey);

    // Apply pagination limits
    const MAX_SHOWS = INITIAL_SHOWS + SHOWS_PER_PAGE * 2;
    const limitedItems = allItems.slice(0, MAX_SHOWS);
    const totalShows = limitedItems.length;
    const displayedCount = INITIAL_SHOWS + (visiblePages - 1) * SHOWS_PER_PAGE;
    const displayedItems = limitedItems.slice(0, displayedCount);
    const remainingShows = Math.max(totalShows - displayedCount, 0);

    return (
      <div>
        <h3 className="mb-3 font-sans font-semibold text-xl normal-case">
          Shows {location?.city ? `near ${location.city}` : "Near You"}
        </h3>

        <div className="flex flex-col gap-4">
          {displayedItems.map((item, index) => {
            const followedComedianIds = subscriptions.map(
              (sub) => sub.comedian_id
            );

            if (item.type === "individual") {
              const show = item.item as ShowWithComedian;
              const comedian = getPrimaryShowComedian(show);
              if (!comedian) return null;

              // For all individual shows, use ResponsiveShowRow consistently
              return (
                <ResponsiveShowRow
                  key={show.id}
                  shows={[processShowData(show)]}
                  showcaseShow={show}
                  comedian={comedian}
                  location="feed"
                  isNearby={false}
                  isFollowed={subscriptions.some(
                    (subscription) => subscription.comedian_id === comedian.id
                  )}
                  followedComedianIds={followedComedianIds}
                />
              );
            } else {
              // Grouped shows
              const group = item.item as {
                comedian: NonNullable<ShowWithComedian["comedian"]>;
                shows: ShowWithComedian[];
              };
              return (
                <ResponsiveShowRow
                  key={group.comedian.id}
                  shows={group.shows.map((show: ShowWithComedian) =>
                    processShowData(show)
                  )}
                  showcaseShow={group.shows[0]}
                  comedian={group.comedian}
                  location="feed"
                  isNearby={false}
                  isFollowed={subscriptions.some(
                    (subscription) =>
                      subscription.comedian_id === group.comedian.id
                  )}
                  followedComedianIds={followedComedianIds}
                />
              );
            }
          })}
        </div>

        {totalShows > INITIAL_SHOWS &&
          remainingShows > 0 &&
          !shouldRedirect && (
            <button
              className={cn(buttonClassName, "mt-4")}
              onClick={() => {
                setVisiblePages((prev) => prev + 1);
              }}
            >
              See more shows ({Math.min(SHOWS_PER_PAGE, remainingShows)})
            </button>
          )}

        {shouldRedirect ||
          (remainingShows === 0 && (
            <Link href="/nearby" className="block w-full mt-4">
              <button
                className={cn(
                  buttonClassName,
                  "mt-4 flex flex-row gap-2 items-center"
                )}
                onClick={() =>
                  trackEvent(FeedEvents.CLICKED_VIEW_ALL_NEARBY_SHOWS, {
                    action: "redirect",
                    total_shows: totalShows,
                  })
                }
              >
                See all shows <ExternalLink width={16} height={16} />
              </button>
            </Link>
          ))}
      </div>
    );
  }

  const totalShows = filteredShows.length;
  const displayedCount = INITIAL_SHOWS + (visiblePages - 1) * SHOWS_PER_PAGE;
  const displayedShows = filteredShows.slice(0, displayedCount);
  const remainingShows = Math.max(totalShows - displayedCount, 0);

  return (
    <div className="my-2 px-4 md:px-0">
      <h3 className="mb-3 font-sans font-semibold text-xl normal-case">
        Shows {location?.city ? `Near ${location.city}` : "Near You"}
      </h3>

      {/* Render individual shows */}
      {displayedShows.map((show) => {
        // Skip if this show should be grouped (will be rendered elsewhere)
        if (!shouldSkipGrouping(show)) {
          return null;
        }

        const comedian = getPrimaryShowComedian(show);
        if (!comedian) return null;

        // For all individual shows, use ResponsiveShowRow consistently
        const followedComedianIds =
          subscriptions?.map((sub) => sub.comedian_id) || [];

        return (
          <ResponsiveShowRow
            key={show.id}
            shows={[processShowData(show)]}
            showcaseShow={show}
            comedian={comedian}
            location="feed"
            isNearby={false}
            isFollowed={
              subscriptions?.some(
                (subscription) => subscription.comedian_id === comedian.id
              ) || false
            }
            followedComedianIds={followedComedianIds}
          />
        );
      })}

      {/* Render grouped shows */}
      {groupedShows.map(({ comedian, shows }) => {
        const followedComedianIds =
          subscriptions?.map((sub) => sub.comedian_id) || [];

        return (
          <ResponsiveShowRow
            key={`${comedian.id}-group`}
            shows={shows.map((show: ShowWithComedian) => processShowData(show))}
            showcaseShow={shows[0]}
            comedian={comedian}
            location="feed"
            isNearby={false}
            isFollowed={
              subscriptions?.some(
                (subscription) => subscription.comedian_id === comedian.id
              ) || false
            }
            followedComedianIds={followedComedianIds}
          />
        );
      })}

      {totalShows > INITIAL_SHOWS && remainingShows > 0 && !shouldRedirect && (
        <button
          className="py-4 w-full border btn-outlined border-slate-gray-900 text-slate-gray-900 md:w-[unset]"
          onClick={() => {
            setVisiblePages((prev) => prev + 1);
            trackEvent(FeedEvents.CLICKED_VIEW_MORE_NEARBY_SHOWS, {
              action: "paginate",
              page_number: visiblePages,
              total_shows: totalShows,
              shows_displayed: Math.min(SHOWS_PER_PAGE, remainingShows),
            });
          }}
        >
          See more shows ({Math.min(SHOWS_PER_PAGE, remainingShows)})
        </button>
      )}

      {shouldRedirect && (
        <Link href="/nearby" className="block w-full mt-4">
          <button
            className="py-4 w-full border btn-outlined border-slate-gray-900 text-slate-gray-900 md:w-[unset]"
            onClick={() =>
              trackEvent(FeedEvents.CLICKED_VIEW_ALL_NEARBY_SHOWS, {
                action: "redirect",
                total_shows: totalShows,
              })
            }
          >
            See all shows ({totalShows})
          </button>
        </Link>
      )}
    </div>
  );
}
